import React, { useState, useEffect, useRef } from 'react';
import axios from '../../../../../axios-instance';
import { Link } from 'react-router-dom';
import {
  CALENDAR_FEATURE_NAME,
  CALENDAR_FEATURE_NAME_LEGACY,
  comingSoonFeatureLabel,
  displayFeatureName,
  isComingSoonFeature,
  isPooledAvailabilityFeature,
} from '../../../../../constants/featureAccess';
import { formatRoundRobinLimit, getPlanDisplayPrice } from '../../../../../utils/planLimits';

/* Features shown directly on the card (in priority order) */
const KEY_FEATURE_NAMES = [
  'Collect Payments (PayPal, Stripe)',
  'Google Meet',
  'Zoom',
  'Microsoft Teams',
  'Zapier',
  'Zapier (Coming Soon)',
  CALENDAR_FEATURE_NAME,
  CALENDAR_FEATURE_NAME_LEGACY,
  'Metrics and Reporting',
  'Group Events',
  'Automated event notifications',
];

const ComingSoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="8" fill="#F0932B" fillOpacity="0.15" />
    <path d="M8 4v4.5l2.5 1.5" stroke="#F0932B" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="8" fill="#45C48A" fillOpacity="0.12" />
    <path d="M4.5 8l2.5 2.5 4-4.5" stroke="#45C48A" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="8" fill="#F4F4F4" />
    <path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="#CCCCCC" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="6.5" stroke="#0C4767" strokeOpacity="0.4" />
    <path d="M7 6v4M7 4.5v.5" stroke="#0C4767" strokeOpacity="0.7" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

/* Tooltip for extra features */
const MoreFeaturesTooltip = ({ features }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const comingSoon = features.filter((f) => isComingSoonFeature(f.feature_name));
  const enabled = features.filter(
    (f) => f.status === 'E' && !isComingSoonFeature(f.feature_name)
  );
  const disabled = features.filter(
    (f) => f.status !== 'E' && !isComingSoonFeature(f.feature_name)
  );

  return (
    <div className="more-tooltip" ref={ref}>
      <button
        className="more-tooltip__trigger"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <InfoIcon />
        <span>+{features.length} more feature{features.length !== 1 ? 's' : ''}</span>
      </button>

      {open && (
        <div className="more-tooltip__panel">
          <p className="more-tooltip__section-label">Included</p>
          {enabled.length === 0 && <p className="more-tooltip__empty">None in this plan</p>}
          {enabled.map((f, i) => (
            <div key={i} className="more-tooltip__row">
              <CheckIcon />
              <span>{displayFeatureName(f.feature_name)}</span>
            </div>
          ))}
          {comingSoon.length > 0 && (
            <>
              <p className="more-tooltip__section-label more-tooltip__section-label--gap">Coming soon</p>
              {comingSoon.map((f, i) => (
                <div key={i} className="more-tooltip__row">
                  <ComingSoonIcon />
                  <span>{comingSoonFeatureLabel(f.feature_name)}</span>
                </div>
              ))}
            </>
          )}
          {disabled.length > 0 && (
            <>
              <p className="more-tooltip__section-label more-tooltip__section-label--gap">Not included</p>
              {disabled.map((f, i) => (
                <div key={i} className="more-tooltip__row more-tooltip__row--disabled">
                  <XIcon />
                  <span>{displayFeatureName(f.feature_name)}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const Pricings = ({ hideCompareLink = false }) => {
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState('monthly');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/admin/plans')
      .then((res) => { setPlans(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filteredPlans = [...plans].sort(
    (a, b) => Number(a.monthly_price) - Number(b.monthly_price)
  );

  const isPopular = (index) => index === 1 || (filteredPlans.length === 1 && index === 0);

  /* Split features into key (shown on card) and extra (in tooltip) */
  const splitFeatures = (features) => {
    const key = [];
    const extra = [];
    const remaining = [...features];

    KEY_FEATURE_NAMES.forEach((name) => {
      const idx = remaining.findIndex((f) => f.feature_name === name);
      if (idx !== -1) {
        key.push(remaining[idx]);
        remaining.splice(idx, 1);
      }
    });

    remaining.forEach((f) => extra.push(f));
    return { key, extra };
  };

  return (
    <section id="pricing" className="pricing-section">
      <div className="pricing-section__inner">
        <div className="pricing-section__header">
          <span className="pricing-section__label">Simple & Transparent</span>
          <h2 className="pricing-section__title">Pricing Plans</h2>
          <p className="pricing-section__subtitle">Start free. Standard and Teams include a 14-day trial — card required, cancel anytime.</p>

          <div className="pricing-billing-toggle">
            <button
              className={`billing-btn${billing === 'monthly' ? ' billing-btn--active' : ''}`}
              onClick={() => setBilling('monthly')}
            >Monthly</button>
            <button
              className={`billing-btn${billing === 'annual' ? ' billing-btn--active' : ''}`}
              onClick={() => setBilling('annual')}
            >
              Annually
              <span className="billing-badge">Save 20%</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="pricing-loading">Loading plans...</div>
        ) : (
          <div className={`pricing-cards pricing-cards--${filteredPlans.length}`}>
            {filteredPlans.map((plan, index) => {
              const popular = isPopular(index);
              const price = getPlanDisplayPrice(plan, billing);
              const isFree = Number(price) === 0;
              const allFeatures = plan.plan_details || [];
              const { key: keyFeatures, extra: extraFeatures } = splitFeatures(allFeatures);

              return (
                <div key={plan.id} className={`pricing-card${popular ? ' pricing-card--popular' : ''}`}>
                  {popular && <div className="pricing-card__badge">Most Popular</div>}

                  <div className="pricing-card__top">
                    <h3 className="pricing-card__name">{plan.plan_name}</h3>
                    <div className="pricing-card__price">
                      {isFree ? (
                        <span className="pricing-card__amount">Free</span>
                      ) : (
                        <>
                          <span className="pricing-card__amount">${price}</span>
                          <span className="pricing-card__period">/mo / user</span>
                        </>
                      )}
                    </div>
                    {billing === 'annual' && !isFree && (
                      <p className="pricing-card__billed">Billed annually</p>
                    )}
                    {!isFree && (
                      <p className="pricing-card__billed">14-day trial · card required</p>
                    )}
                  </div>

                  <Link to="/signup">
                    <button className={`pricing-card__cta${popular ? ' pricing-card__cta--primary' : ''}`}>
                      {isFree ? 'Get Started Free' : 'Start 14-day trial'}
                    </button>
                  </Link>

                  {(plan.no_of_calendars || plan.no_of_events != null) && (
                    <div className="pricing-card__meta">
                      {plan.no_of_calendars && (
                        <div className="pricing-card__meta-item">
                          <CheckIcon />
                          <span>{plan.no_of_calendars} calendar{plan.no_of_calendars > 1 ? 's' : ''}/user</span>
                        </div>
                      )}
                      {plan.no_of_events != null && (
                        <div className="pricing-card__meta-item">
                          <CheckIcon />
                          <span>{(plan.no_of_events < 0) ? 'Unlimited events' : `${plan.no_of_events} event${plan.no_of_events !== 1 ? 's' : ''}`}</span>
                        </div>
                      )}
                      {(plan.plan_details || []).some(
                        (d) => isPooledAvailabilityFeature(d.feature_name) && d.status === 'E'
                      ) && (
                        <div className="pricing-card__meta-item">
                          <CheckIcon />
                          <span>
                            Round robin: {formatRoundRobinLimit(plan.no_of_round_robin_events, true)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {keyFeatures.length > 0 && (
                    <div className="pricing-card__features">
                      <p className="pricing-card__features-label">Key features</p>
                      <ul>
                        {keyFeatures.map((f, fi) => {
                          const soon = isComingSoonFeature(f.feature_name);
                          return (
                          <li key={fi} className="pricing-card__feature">
                            {soon ? <ComingSoonIcon /> : f.status === 'E' ? <CheckIcon /> : <XIcon />}
                            <span className={!soon && f.status !== 'E' ? 'pricing-card__feature--disabled' : ''}>
                              {displayFeatureName(f.feature_name)}
                            </span>
                          </li>
                          );
                        })}
                      </ul>

                      {extraFeatures.length > 0 && (
                        <MoreFeaturesTooltip features={extraFeatures} />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!hideCompareLink && (
          <div className="pricing-section__compare">
            <Link to="/pricing#compare" className="pricing-compare-link">
              Compare all plans
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};
